<script setup lang="ts">
/**
 * The template's illustrated icon sitting in its rounded tile, as on the feature
 * grid and the service cards.
 *
 * The tile keeps its light surface in dark mode on purpose: the illustrations
 * are dark line art with a yellow accent and would disappear on a dark square.
 * That also makes the tile usable on the ink-coloured blocks, where it reads as
 * a small light card rather than an icon that vanished.
 *
 * The illustration is decorative — the label next to it carries the meaning —
 * so it is hidden from assistive technology.
 */
withDefaults(defineProps<{
  /** Path under /sofax, e.g. `/sofax/service-icon-2.svg`. */
  src: string
  size?: 'sm' | 'md' | 'lg'
  /** `white` for the grey `surface-alt` sections, where a surface tile blends in. */
  tone?: 'surface' | 'white'
  /** Lifts slightly when an ancestor marked `.group` is hovered. */
  interactive?: boolean
}>(), {
  size: 'md',
  tone: 'surface',
})

const boxSizes = {
  sm: 'size-12',
  md: 'size-16',
  lg: 'size-20',
} as const

const artSizes = {
  sm: 'size-7',
  md: 'size-10',
  lg: 'size-12',
} as const
</script>

<template>
  <span
    class="inline-flex shrink-0 items-center justify-center rounded-[0.625rem] transition-transform duration-300"
    :class="[
      boxSizes[size],
      tone === 'white' ? 'bg-white' : 'bg-surface',
      interactive ? 'motion-safe:group-hover:-rotate-6 motion-safe:group-hover:scale-105' : '',
    ]"
  >
    <!-- The size class alone sets the box: pairing it with `w-auto` let the
         intrinsic SVG size win and the art collapsed inside the tile. -->
    <!-- Not lazy on purpose: these are 1–3 KB of SVG, and in the scaled-down
         previews and screenshots a lazy image never enters a viewport, so the
         tile would stay empty wherever the page is not actually scrolled. -->
    <img :src="src" alt="" aria-hidden="true" width="80" height="80" :class="artSizes[size]">
  </span>
</template>
